import compact from 'lodash/compact';
import sumBy from 'lodash/sumBy';

import { ZigzagEvent } from '@common/log-manager';
import { airbridgeTracker } from '@common/marketing-tracker';

import { GetPdpBaseInfo_catalog_product_base_info_product_managed_category_list } from '../graphql/pdp-base-info_types.2';
import { trackAppsflyerPba } from './tracking/appsflyer_pba';
import { FbPixelEventEnum, trackFbPixel } from './tracking/facebook_pixel';
import { trackGa4 } from './tracking/ga4';
import { trackKakaoPixel } from './tracking/kakao_pixel';

function generateCategoriesForAirbridge(
  categories: (GetPdpBaseInfo_catalog_product_base_info_product_managed_category_list | null)[] | null,
) {
  if (!categories) return {};

  const [mainCategory] = categories;
  const usedCategories = compact(categories);
  const combinedCategoryId = usedCategories.map(({ category_id }) => category_id).join(', ');
  const combinedCategoryName = usedCategories.map(({ value }) => value).join(', ');

  return {
    mainCategory,
    combinedCategoryId,
    combinedCategoryName,
  };
}

export const usePdpWebEvents = () => {
  const currency = 'KRW';

  const pageViewEvent = (params: PageViewParams, { enabledAirbridge = true }: PageViewOptions = {}) => {
    if (currency === 'KRW') {
      trackAppsflyerPba({
        eventType: 'EVENT',
        eventName: 'af_content_view',
        eventValue: {
          content_id: params?.product_id,
          currency,
          af_price: params?.price,
        },
      });

      if (enabledAirbridge) {
        const { mainCategory, combinedCategoryId, combinedCategoryName } = generateCategoriesForAirbridge(
          params.managedCategoryList,
        );

        airbridgeTracker.events.send('airbridge.ecommerce.product.viewed', {
          value: params.price,
          label: mainCategory?.value,
          semanticAttributes: {
            products: [
              {
                productID: params.product_id,
                name: params.product_name,
                categoryID: combinedCategoryId,
                categoryName: combinedCategoryName,
                brandID: params.shop_id,
                brandName: params.shop_name,
                price: params.price,
                currency,
              },
            ],
          },
        });
      }

      trackGa4('view_item', {
        currency,
        value: params?.price,
        items: [
          {
            item_id: params?.product_id,
            item_name: params?.product_name,
            price: params?.price,
            quantity: params?.quantity,
          },
        ],
      });
      trackFbPixel(FbPixelEventEnum.ViewContent);
      trackKakaoPixel('viewContent', { id: params?.product_id });

      ZigzagEvent.logAppsFlyer('af_content_view', {
        af_content_id: params?.product_id,
        af_content_type: params?.category,
        af_currency: currency,
        af_price: params?.price,
        shop_id: params.shop_id,
      });

      ZigzagEvent.logFacebook('fb_mobile_content_view', {
        fb_content_id: params?.product_id,
        fb_content_type: params.category,
        fb_currency: currency,
        _valueToSum: params?.price,
        shop_id: params?.shop_id,
      });

      ZigzagEvent.logFirebase('view_item', {
        item_id: params?.product_id,
        item_category: params.category,
        currency,
        value: params?.price,
        shop_id: params?.shop_id,
      });
    }
  };

  const addToCartEvent = (params: AddToCartParams) => {
    if (currency === 'KRW') {
      const { mainCategory, combinedCategoryId, combinedCategoryName } = generateCategoriesForAirbridge(
        params.managedCategoryList,
      );

      trackAppsflyerPba({
        eventType: 'EVENT',
        eventName: 'af_add_to_cart',
        eventValue: {
          af_content_id: params?.product_id,
          af_content_type: params?.category,
          af_currency: currency,
          af_price: params?.price,
          shop_id: params?.shop_id,
        },
      });

      airbridgeTracker.events.send('airbridge.ecommerce.product.addedToCart', {
        label: mainCategory?.value,
        semanticAttributes: {
          totalValue: sumBy(params.items, ({ price, quantity }) => price * quantity),
          currency,
          products: params.items.map((item) => ({
            productID: item.item_id,
            name: item.item_name,
            categoryID: combinedCategoryId,
            categoryName: combinedCategoryName,
            brandID: params.shop_name,
            currency,
            price: item.price,
            quantity: item.quantity,
          })),
        },
      });

      trackGa4('add_to_cart', {
        currency,
        value: params?.price,
        items: params?.items,
      });
      trackFbPixel(FbPixelEventEnum.AddToCart);
      trackKakaoPixel('addToCart', { id: params?.product_id });

      ZigzagEvent.logAppsFlyer('af_add_to_cart', {
        af_content_id: params?.product_id,
        af_content_type: params?.category,
        af_currency: currency,
        af_price: params?.price,
        shop_id: params?.shop_id,
      });
      ZigzagEvent.logFacebook('fb_mobile_add_to_cart', {
        fb_content: params?.product_id,
        fb_content_type: 'product',
        fb_currency: currency,
        _valueToSum: params?.price,
        shop_id: params?.shop_id,
      });
      ZigzagEvent.logFirebase('add_to_cart', {
        item_id: params?.product_id,
        item_category: params?.category,
        currency,
        value: params?.price,
        shop_id: params?.shop_id,
      });
    }
  };

  const shareEvent = (params: ShareEventParams) => {
    if (currency === 'KRW') {
      trackGa4('share', {
        /**
         * link | kakao | facebook
         */
        method: params?.method,
        /**
         * product | promotion
         */
        content_type: params?.content_type,
        item_id: params?.product_id,
      });
    }
  };

  return {
    pageViewEvent,
    addToCartEvent,
    shareEvent,
  };
};

interface PageViewParams {
  product_id: string;
  price: number;
  product_name: string;
  quantity: number;
  category: string;
  shop_id: string;
  shop_name: string;
  managedCategoryList: (GetPdpBaseInfo_catalog_product_base_info_product_managed_category_list | null)[] | null;
}

interface PageViewOptions {
  enabledAirbridge?: boolean;
}

interface AddToCartParams {
  managedCategoryList: (GetPdpBaseInfo_catalog_product_base_info_product_managed_category_list | null)[] | null;
  product_name: string;
  product_id: string;
  price: number;
  items: CartItem[];
  category: string;
  shop_id: string;
  shop_name: string;
}

interface CartItem {
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
}

interface ShareEventParams {
  method: string;
  content_type: string;
  product_id: string;
}
